import { EventData } from 'features/systemLens'
import {
  generatePlotlyData,
  generatePlotlyLayout,
  SENSOR_COLORS_HEX,
  useScatterplotContext,
} from 'features/timeSeries'
import { useMemo } from 'react'
import { EnhancedSensorEntry } from 'types'

interface ThresholdValue {
  value: number
  type: 'lowerLower' | 'lower' | 'upper' | 'upperUpper'
  sensors: string[]
}

interface UsePlotlyGeneratorsProps {
  operationalEvents: EventData[]
  enhancedSensorEntries: EnhancedSensorEntry[]
  uniqueThresholds: ThresholdValue[]
  allSensorsToRender: Array<{ id: string; name: string }>
}

export const usePlotlyGenerators = ({
  operationalEvents,
  enhancedSensorEntries,
  uniqueThresholds,
  allSensorsToRender,
}: UsePlotlyGeneratorsProps) => {
  const {
    chartData,
    derivedValues,
    regressionConfigs,
    regressionResults,
    unitMapping,
    sensorRanges,
    singleScaleMode,
    connectPoints,
    showXAxis,
    xAxisSensorId,
    customAxisRanges,
    customXAxisRange,
    sensorAxisMapping,
    dataLayers,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
  } = useScatterplotContext()

  const plotlyLayout = useMemo(() => {
    return generatePlotlyLayout({
      sensorAxisMapping,
      dataLayers,
      operationalEvents,
      singleScaleMode,
      allSensorsToRender,
      uniqueThresholds,
      enhancedSensorEntries,
      customAxisRanges,
      sensorRanges,
      sensorColors: SENSOR_COLORS_HEX,
      derivedValues,
      regressionConfigs,
      xAxisSensorId: xAxisSensorId || undefined,
      customXAxisRange,
      unitMapping,
      customSensorColors,
      showXAxis,
    })
  }, [
    sensorAxisMapping,
    dataLayers,
    operationalEvents,
    singleScaleMode,
    allSensorsToRender,
    uniqueThresholds,
    enhancedSensorEntries,
    customAxisRanges,
    sensorRanges,
    derivedValues,
    regressionConfigs,
    xAxisSensorId,
    customXAxisRange,
    unitMapping,
    customSensorColors,
    showXAxis,
  ])

  const plotlyData = useMemo(() => {
    return generatePlotlyData({
      dataLayers,
      chartData,
      allSensorsToRender,
      enhancedSensorEntries,
      unitMapping,
      sensorAxisMapping,
      regressionConfigs,
      regressionResults,
      derivedValues,
      xAxisSensorId,
      connectPoints,
      sensorVisibilityConfig,
      customSensorColors,
      highlightedSensorId,
    })
  }, [
    dataLayers,
    chartData,
    allSensorsToRender,
    enhancedSensorEntries,
    unitMapping,
    sensorAxisMapping,
    regressionConfigs,
    regressionResults,
    derivedValues,
    xAxisSensorId,
    connectPoints,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
  ])

  const hasDataPoints = useMemo(() => {
    return plotlyData.some((trace) => {
      return (
        (Array.isArray(trace.x) && trace.x.length > 0) ||
        (Array.isArray(trace.y) && trace.y.length > 0)
      )
    })
  }, [plotlyData])

  return {
    plotlyLayout,
    plotlyData,
    hasDataPoints,
  }
}
